const LABELS = {
  fr: {
    aromeLabel: 'AROME 48h',
    aromeDetail: 'Haute précision court terme',
    openMeteoLabel: 'Open-Meteo',
    openMeteoDetail: 'Modèle best match',
    mountainTitle: 'Conditions montagne',
    coastalTitle: 'Conditions littorales',
    gusts: 'Rafales',
    visibility: 'Visibilité',
    freshSnow: 'Neige fraîche',
    snowDepth: 'Neige au sol',
    freezingLevel: 'Limite pluie-neige',
    thunder: 'Risque orage',
    seaTemperature: 'Mer',
    waveHeight: 'Vagues',
    wavePeriod: 'Période',
    wind: 'Vent',
    uv: 'UV',
  },
  en: {
    aromeLabel: 'AROME 48h',
    aromeDetail: 'High precision short range',
    openMeteoLabel: 'Open-Meteo',
    openMeteoDetail: 'Best match model',
    mountainTitle: 'Mountain conditions',
    coastalTitle: 'Coastal conditions',
    gusts: 'Gusts',
    visibility: 'Visibility',
    freshSnow: 'Fresh snow',
    snowDepth: 'Snow depth',
    freezingLevel: 'Freezing level',
    thunder: 'Thunderstorm risk',
    seaTemperature: 'Sea',
    waveHeight: 'Waves',
    wavePeriod: 'Period',
    wind: 'Wind',
    uv: 'UV',
  },
};

const getLabels = (language = 'fr') => LABELS[language] || LABELS.fr;

const appendOptionalParam = (params, key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    params.append(key, String(value));
  }
};

const numberValues = (values = []) => values.filter((value) => typeof value === 'number' && Number.isFinite(value));

const maxNumber = (values = []) => {
  const numbers = numberValues(values);
  return numbers.length ? Math.max(...numbers) : null;
};

const minNumber = (values = []) => {
  const numbers = numberValues(values);
  return numbers.length ? Math.min(...numbers) : null;
};

const sumNumbers = (values = []) => numberValues(values).reduce((sum, value) => sum + value, 0);

const sliceDaily = (daily = {}, start, end) => (
  Object.entries(daily).reduce((result, [key, value]) => {
    result[key] = Array.isArray(value) ? value.slice(start, end) : value;
    return result;
  }, {})
);

export function buildWeatherRequestParams(parkCoordinates = {}, language = 'fr') {
  const latitude = parkCoordinates.latitude ?? parkCoordinates.lat;
  const longitude = parkCoordinates.longitude ?? parkCoordinates.lng;
  const params = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    lang: language,
  });

  appendOptionalParam(params, 'country', parkCoordinates.country);
  appendOptionalParam(params, 'country_code', parkCoordinates.country_code || parkCoordinates.countryCode);
  appendOptionalParam(params, 'admin1', parkCoordinates.admin1);
  appendOptionalParam(params, 'elevation', parkCoordinates.elevation);

  return params;
}

export function getWeatherModelBadge(modelStrategy = {}, language = 'fr') {
  const labels = getLabels(language);
  if (modelStrategy?.shortRangeApplied) {
    return {
      label: labels.aromeLabel,
      detail: labels.aromeDetail,
    };
  }

  return {
    label: labels.openMeteoLabel,
    detail: labels.openMeteoDetail,
  };
}

export function shouldFetchMarineSummary(weatherData) {
  return Boolean(weatherData?.locationProfile?.isCoastalCandidate);
}

export function buildWeatherContextPanels(weatherData, marineSummary, language = 'fr') {
  const labels = getLabels(language);
  const panels = [];
  const hourly = weatherData?.hourly || {};
  const current = weatherData?.current || {};

  if (weatherData?.locationProfile?.terrain === 'mountain') {
    const gusts = maxNumber([current.wind_gusts_10m, ...(hourly.wind_gusts_10m || [])]);
    const visibility = minNumber(hourly.visibility);
    const freshSnow = sumNumbers(hourly.snowfall);
    const snowDepth = maxNumber(hourly.snow_depth);
    const freezingLevel = minNumber(hourly.freezing_level_height);
    const thunder = maxNumber(hourly.thunderstorm_probability);
    const items = [];

    if (gusts !== null) items.push({ key: 'gusts', label: labels.gusts, value: `${Math.round(gusts)} km/h` });
    if (visibility !== null) items.push({ key: 'visibility', label: labels.visibility, value: `${(visibility / 1000).toFixed(1)} km` });
    if (freshSnow > 0) items.push({ key: 'freshSnow', label: labels.freshSnow, value: `${freshSnow.toFixed(1)} cm` });
    if (snowDepth !== null) items.push({ key: 'snowDepth', label: labels.snowDepth, value: `${Math.round(snowDepth)} cm` });
    if (freezingLevel !== null) items.push({ key: 'freezingLevel', label: labels.freezingLevel, value: `${Math.round(freezingLevel)} m` });
    if (thunder !== null) items.push({ key: 'thunder', label: labels.thunder, value: `${Math.round(thunder)}%` });

    if (items.length) {
      panels.push({ key: 'mountain', title: labels.mountainTitle, items });
    }
  }

  if (marineSummary?.available) {
    const items = [];
    if (typeof marineSummary.current?.seaSurfaceTemperature === 'number') {
      items.push({ key: 'seaTemperature', label: labels.seaTemperature, value: `${Math.round(marineSummary.current.seaSurfaceTemperature)}°C` });
    }
    if (typeof marineSummary.max?.waveHeight === 'number') {
      items.push({ key: 'waveHeight', label: labels.waveHeight, value: `${marineSummary.max.waveHeight.toFixed(1)} m` });
    }
    if (typeof marineSummary.max?.wavePeriod === 'number') {
      items.push({ key: 'wavePeriod', label: labels.wavePeriod, value: `${Math.round(marineSummary.max.wavePeriod)} s` });
    }
    if (typeof current.wind_speed_10m === 'number') {
      items.push({ key: 'wind', label: labels.wind, value: `${Math.round(current.wind_speed_10m)} km/h` });
    }
    if (typeof current.wind_gusts_10m === 'number') {
      items.push({ key: 'gusts', label: labels.gusts, value: `${Math.round(current.wind_gusts_10m)} km/h` });
    }
    if (typeof current.uv_index === 'number') {
      items.push({ key: 'uv', label: labels.uv, value: `${Math.round(current.uv_index)}` });
    }

    if (items.length) {
      panels.push({ key: 'coastal', title: labels.coastalTitle, items });
    }
  }

  return panels;
}

export function getDailyForecastSections(weatherData = {}) {
  const daily = weatherData.daily || {};
  const time = Array.isArray(daily.time) ? daily.time : [];
  const forecastEnd = Math.min(7, time.length);
  const forecast = sliceDaily(daily, 0, forecastEnd);
  const trend = sliceDaily(daily, forecastEnd, time.length);

  forecast.confidenceByDay = (weatherData.confidenceByDay || []).slice(0, forecastEnd);
  trend.confidenceByDay = (weatherData.confidenceByDay || []).slice(forecastEnd);

  return { forecast, trend };
}
